import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { PortalPopover } from './PopoverStyled';

/**
 * Popover de grupo (modo collapsed), renderizado via portal.
 * Estilo vem do PortalPopover — classes .portal-popover-*
 */
function GroupPopover({
   group,
   visibleChildren,
   isActive,
   onNavigate = undefined,
   onClose,
   anchorRef = null,
}) {
   const popoverRef = useRef(null);
   const [coords, setCoords] = useState({ top: 0, left: 0 });

   useLayoutEffect(() => {
      if (anchorRef?.current) {
         const rect = anchorRef.current.getBoundingClientRect();
         setCoords({ top: rect.top, left: rect.right + 10 });
      }
   }, [anchorRef]);

   useEffect(() => {
      const handler = (e) => {
         if (
            popoverRef.current &&
            !popoverRef.current.contains(e.target) &&
            anchorRef?.current &&
            !anchorRef.current.contains(e.target)
         ) {
            onClose();
         }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
   }, [onClose, anchorRef]);

   return ReactDOM.createPortal(
      <PortalPopover
         ref={popoverRef}
         role="menu"
         $top={coords.top}
         $left={coords.left}
      >
         <p className="portal-popover-title">{group.label}</p>

         {visibleChildren.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            return (
               <Link
                  key={item.to}
                  to={item.to}
                  role="menuitem"
                  title={item.label}
                  className={`portal-popover-item ${active ? 'active' : ''}`}
                  onClick={() => {
                     if (onNavigate) onNavigate();
                     onClose();
                  }}
               >
                  <div className="portal-popover-icon">
                     <Icon size={14} />
                  </div>
                  <span className="portal-popover-label">{item.label}</span>
                  {item.newItem && (
                     <span className="portal-popover-new-badge">Novo!</span>
                  )}
                  {active && <span className="portal-popover-active-dot" />}
               </Link>
            );
         })}
      </PortalPopover>,
      document.body
   );
}

GroupPopover.propTypes = {
   group: PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
   }).isRequired,
   visibleChildren: PropTypes.arrayOf(
      PropTypes.shape({
         to: PropTypes.string.isRequired,
         label: PropTypes.string.isRequired,
         icon: PropTypes.elementType.isRequired,
         newItem: PropTypes.bool,
      })
   ).isRequired,
   isActive: PropTypes.func.isRequired,
   onNavigate: PropTypes.func,
   onClose: PropTypes.func.isRequired,
   anchorRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

export default GroupPopover;
